import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { UnitDataRow, UnitTypeName } from '../types/unitListTypes';
import { PropertyBuildingsQuery } from '../../../../types/generated-types';
import { useLocation, useNavigate } from 'react-router-dom';
import React from 'react';
import { buildUnitDetailPath } from './unitListNavigation';
import { buildUnitTableContainerStyle } from './unitTableLayout';

export const MyUnitsTable = ({
  tableColumns,
  tableRows,
  updateSearchFilters,
  screenBreakLevel,
  isSmall,
  safeTopPadding,
  showSearch,
  showFilter,
  buildingsData,
  sortColumnName,
  sortDescending,
}: {
  tableColumns: MRT_ColumnDef<UnitDataRow>[];
  tableRows: UnitDataRow[];
  updateSearchFilters: (newSearchFilters: {
    newSearchTerm?: string;
    newSelectedBuildingIds?: string[];
    newSelectedFloorIds?: string[];
    newSelectedUnitType?: UnitTypeName;
    newSortColumnName?: string;
    newSortDescending?: string;
  }) => void;
  screenBreakLevel: number;
  isSmall: boolean;
  safeTopPadding: number;
  showSearch: boolean;
  showFilter: boolean;
  buildingsData: PropertyBuildingsQuery;
  sortColumnName: string;
  sortDescending: boolean;
}) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const table = useMaterialReactTable({
    layoutMode: 'grid',
    defaultColumn: {
      minSize: 100,
      size: 100,
      enableColumnActions: false,
      sortingFn: 'alphanumeric',
      muiTableHeadCellProps: {
        align: 'left',
      },
      muiTableBodyCellProps: {
        align: 'left',
      },
    },
    columns: tableColumns,
    data: tableRows, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.
    // enableColumnFilterModes: true,
    // enableColumnOrdering: true,
    // enableGrouping: true,
    // enableColumnPinning: true,
    // enableFacetedValues: true,
    // enableRowActions: true,
    // enableRowSelection: true,
    enableStickyHeader: true,
    enableDensityToggle: true,
    initialState: {
      density: 'compact',
      showColumnFilters: false,
      showGlobalFilter: false,
      columnOrder: [
        'plmIndicators',
        'status',
        'name',
        'buildingName',
        'floorName',
        'temperature',
        'setpoint',
        'humidity',
        'setpointLimitProfile',
        'installation',
        'loopSupply',
        'loopReturn',
        'delta',
      ],
    },
    onSortingChange: (updater) => {
      // updater can be either a new sorting state or a function
      const newSorting =
        typeof updater === 'function'
          ? updater(table.getState().sorting)
          : updater;

      // Update URL with new sorting - handle empty sorting array
      if (newSorting && newSorting.length > 0) {
        updateSearchFilters({
          newSortColumnName: newSorting[0].id ?? 'name',
          newSortDescending: newSorting[0].desc ? 'true' : 'false',
        });
      } else {
        // No sorting - reset to default
        updateSearchFilters({
          newSortColumnName: 'name',
          newSortDescending: 'false',
        });
      }

      // Table state will be updated via search parameters.
    },
    renderTopToolbar: false,
    renderBottomToolbar: false,
    enablePagination: false,
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    muiTableBodyProps: {
      sx: {
        '& tr:nth-of-type(even)': {
          backgroundColor: 'rgba(241, 241, 241, 0.3) !important',
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (row.original?.id) {
          navigate(buildUnitDetailPath(row.original.id, search));
        }
      },
      sx: {
        height: isSmall ? '100%' : screenBreakLevel > 4 ? '37px' : undefined,
      },
    }),
    muiTableHeadRowProps: {
      sx: {
        height: screenBreakLevel > 4 ? '37px' : undefined,
        '& .Mui-TableHeadCell-Content-Labels': {
          paddingLeft: '0px',
        },
      },
    },
    muiTableHeadCellProps: {
      sx: {
        padding: '0px',
        '& .MuiTableHeadCell-Content': {
          minWidth: '100px',
        },
        '& .MuiTableHeadCell-Content-Labels': {
          paddingLeft: '0px',
        },
      },
    },
    muiTableBodyCellProps: {
      style: {
        padding: '0px',
      },
    },
    muiTableContainerProps: {
      style: buildUnitTableContainerStyle({
        isSmall,
        safeTopPadding,
        showSearch,
        showFilter,
        screenBreakLevel,
        buildingCount: buildingsData?.propertyById?.buildings?.length ?? 0,
        floorCount: buildingsData?.propertyById?.floors?.length ?? 0,
      }),
    },
    // TODO: Peter ... are we going to use the bottom toolbar here? If not, remove this.
    // muiBottomToolbarProps: {
    //   sx: {
    //     minHeight: '40px',
    //     '& .MuiBox-root': {
    //       paddingTop: '0px',
    //       paddingBottom: '0px',
    //       minHeight: '0px',
    //     },
    //     '& .MuiTablePagination-root': {
    //       padding: '0px',
    //     },
    //   },
    // },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30],
      shape: 'rounded',
      variant: 'outlined',
    },
    state: {
      sorting: [
        {
          id: sortColumnName ?? 'name', //sort by age by default on page load
          desc: sortDescending ?? false,
        },
      ],
    },
  });

  return <MaterialReactTable table={table} />;
};
